import * as yup from 'yup';

// SessionStorage keys
export const SESSION_DRAFT_KEY = 'sampleRequestSessionDraft';
export const CHAT_DRAFT_KEY = 'chatSampleRequestDraft';

// Validation schema
export const sampleRequestSchema = yup.object({
  company_name: yup.string().required('Company name is required'),
  contact_person: yup.string().required('Contact person is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string(),
  country_code: yup.string().max(2), // Brand country code (ISO 3166-1 alpha-2)
  password: yup.string().min(6, 'Password must be at least 6 characters').nullable(), // Optional password for registration
  product_name: yup.string().required('Product name is required'),
  volume: yup.string(),
  estimated_launch_quantity: yup.string().nullable(),  // Text type for flexible input (e.g., "5,000", "10,000-20,000")
  target_cost_budget: yup.string().nullable(),  // Text type for flexible input (e.g., "3.50", "3.00-5.00")
  target_market_country: yup.string(),
  product_type: yup.string().required('Product type is required'),
  efficacy_function: yup.string(),
  formula_texture: yup.string(),
  key_promotional_ingredients: yup.string(),
  restricted_ingredients: yup.string(),
  marketing_brand_claims: yup.string(),
  fragrance: yup.string(),
  color: yup.string(),
  fragrance_color: yup.string(), // Legacy field
  primary_packaging: yup.string(),
  secondary_packaging: yup.string(),
  package_reference_link: yup.string()
    .transform((value) => {
      if (!value) return value;
      if (value && !value.match(/^https?:\/\//i)) {
        return `https://${value}`;
      }
      return value;
    })
    .url('Invalid URL')
    .nullable(),
  benchmark_product_name: yup.string(),
  benchmark_product_link: yup.string()
    .transform((value) => {
      if (!value) return value;
      if (value && !value.match(/^https?:\/\//i)) {
        return `https://${value}`;
      }
      return value;
    })
    .url('Invalid URL')
    .nullable(),
  preferred_attributes: yup.string(),
  non_preferred_attributes: yup.string(),
  additional_requests_comments: yup.string(),
});

// Infer type from schema
export type SampleRequestFormType = yup.InferType<typeof sampleRequestSchema>;

// Draft data type with metadata (for sessionStorage)
export interface SessionDraftData extends Partial<SampleRequestFormType> {
  _savedAt?: string;
  _emailVerified?: boolean;
}

// Server-saved draft type
export interface ServerDraft {
  id: number;
  request_code: string;
  status: 'saved';
  created_at?: string;
  updated_at?: string;
  [key: string]: any;
}

// 중복 제출 감지 시 백엔드가 409(SAMPLE_413) 응답 data 로 반환하는 기존 건 요약
export interface DuplicateSubmissionInfo {
  id: number;
  existing_request_code: string;
  product_name: string;
  status: 'requested' | 'completed' | 'rejected' | 'saved' | 'cancelled';
  created_at: string;
}

// Form field names for loading from URL params or chat
export const SAMPLE_REQUEST_FIELDS = [
  'company_name', 'contact_person', 'email', 'phone', 'country_code',
  'product_name', 'product_type', 'volume',
  'target_market_country', 'estimated_launch_quantity', 'target_cost_budget',
  'efficacy_function', 'key_promotional_ingredients', 'restricted_ingredients', 'formula_texture',
  'fragrance', 'color', 'primary_packaging', 'secondary_packaging', 'package_reference_link',
  'benchmark_product_name', 'benchmark_product_link',
  'preferred_attributes', 'non_preferred_attributes',
  'marketing_brand_claims', 'additional_requests_comments'
] as const;

// D건 프리필 query — 값은 slug, 폼이 공개 상세를 조회해 매핑
export const PREFILL_FORMULATION_PARAM = 'formulation';

// 프리필 query 제거본 — 제출 뒤 남으면 다음 New Request 진입 시 같은 값이 다시 채워짐
export const withoutPrefillParams = (params: URLSearchParams): URLSearchParams => {
  const next = new URLSearchParams(params);
  [...SAMPLE_REQUEST_FIELDS, PREFILL_FORMULATION_PARAM].forEach(key => next.delete(key));
  return next;
};

// System fields to exclude when loading drafts
export const DRAFT_EXCLUDE_FIELDS = [
  'id', 'request_code', 'created_at', 'updated_at',
  'status', 'admin_notes', 'rejection_reason',
  'brand_id', 'manufacturer_id',
  'email_verified', 'project_id', 'image_url', 'image_file',
  'storage_key', 'version', 'is_latest', 'batch_group_id',
];

// Product type 표준 카테고리 목록 — 단일/멀티/챗 폼 + 백엔드 AI 프롬프트가 공유하는 single source of truth.
// 제품 카테고리 기준 (물리적 형태 아님). 백엔드 image_analysis 프롬프트 enum 과 동일하게 유지.
export const PRODUCT_TYPES = [
  'Serum', 'Ampoule', 'Essence', 'Toner', 'Cream', 'Lotion', 'Gel', 'Oil',
  'Mask', 'Eye Patch', 'Eye Cream', 'Cleanser', 'Sunscreen', 'Mist',
  'Foundation', 'Powder', 'Lip Product', 'Body Care', 'Hair Care', 'Other',
];

// AI 분석/외부 입력의 자유서술 product_type 을 표준 카테고리로 매칭하는 동의어 규칙.
// 순서 중요 — 더 구체적인 규칙(eye/lip/sun)을 generic(cream/gel/oil) 보다 먼저 검사.
const PRODUCT_TYPE_SYNONYMS: { type: string; keywords: string[] }[] = [
  { type: 'Eye Patch', keywords: ['eye patch', 'eye mask', 'under eye', 'under-eye', 'eye pad', 'eye gel patch', 'hydrogel patch', 'eye zone'] },
  { type: 'Eye Cream', keywords: ['eye cream', 'eye serum', 'eye care', 'eye product', 'eye gel', 'eye'] },
  { type: 'Lip Product', keywords: ['lipstick', 'lip balm', 'lip gloss', 'lip tint', 'lip mask', 'lip care', 'lip'] },
  { type: 'Sunscreen', keywords: ['sunscreen', 'sun cream', 'sunblock', 'sun block', 'suncare', 'sun care', 'spf', 'uv'] },
  // 'wash-off mask' 가 Cleanser 의 'wash' 에 먼저 걸리지 않도록 Cleanser 앞에서 판정
  { type: 'Mask', keywords: ['wash-off', 'wash off', 'peel-off', 'peel off'] },
  { type: 'Cleanser', keywords: ['cleanser', 'cleansing', 'cleanse', 'face wash', 'foam', 'micellar', 'wash'] },
  { type: 'Ampoule', keywords: ['ampoule', 'ampule', 'ampoul'] },
  { type: 'Essence', keywords: ['essence'] },
  { type: 'Toner', keywords: ['toner', 'tonic', 'toning'] },
  { type: 'Mist', keywords: ['mist', 'face spray', 'facial spray'] },
  { type: 'Foundation', keywords: ['foundation', 'bb cream', 'cc cream', 'cushion', 'concealer', 'makeup base', 'make-up base', 'primer'] },
  { type: 'Powder', keywords: ['powder'] },
  { type: 'Hair Care', keywords: ['hair', 'shampoo', 'conditioner', 'scalp'] },
  { type: 'Body Care', keywords: ['body', 'hand cream', 'hand lotion', 'foot'] },
  { type: 'Mask', keywords: ['mask', 'sheet mask', 'pack', 'sleeping', 'clay', 'modeling'] },
  { type: 'Serum', keywords: ['serum', 'booster'] },
  { type: 'Lotion', keywords: ['lotion', 'emulsion', 'milk'] },
  { type: 'Cream', keywords: ['cream', 'creme', 'crème', 'moisturizer', 'moisturiser', 'balm', 'butter'] },
  { type: 'Gel', keywords: ['gel', 'jelly', 'hydrogel'] },
  { type: 'Oil', keywords: ['oil'] },
];

/**
 * AI 분석 등으로 들어온 자유서술 product_type 을 PRODUCT_TYPES 표준값으로 매칭.
 * 1) 표준값과 정확 일치(대소문자 무관) → 표준 표기, 2) 동의어 키워드 매칭, 3) 실패 시 'Other'.
 * 빈 입력은 빈 문자열 유지(미선택).
 */
export const normalizeProductType = (raw?: string | null): string => {
  const value = (raw ?? '').trim();
  if (!value) return '';
  const lower = value.toLowerCase();
  const exact = PRODUCT_TYPES.find(t => t.toLowerCase() === lower);
  if (exact) return exact;
  // "oil-free" / "oil control" 등은 오일 제품이 아니므로 Oil 매칭에서 제외
  const isOilNegative = /\boil[-\s]?(?:free|control)\b/.test(lower);
  // 단어 경계 매칭으로 부분문자열 오분류 방지 (예: "lipid"→Lip, "hydrating"→X)
  const hasKeyword = (k: string) => new RegExp(`\\b${k}\\b`).test(lower);
  for (const { type, keywords } of PRODUCT_TYPE_SYNONYMS) {
    if (type === 'Oil' && isOilNegative) continue;
    if (keywords.some(hasKeyword)) return type;
  }
  return 'Other';
};

// Country options
export const COUNTRIES = [
  'USA', 'Canada', 'UK', 'Germany', 'France', 'Italy', 'Spain', 'Japan',
  'China', 'Australia', 'Other'
];

// Email verification state
export interface EmailVerificationState {
  emailVerified: boolean;
  emailVerificationSent: boolean;
  verificationCode: string;
  isVerifyingEmail: boolean;
  isSendingVerification: boolean;
}

// File upload state
export interface FileUploadState {
  selectedFile: File | null;
  filePreview: string | null;
}
